import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import {
  getAllCarInsurance,
  deleteCarInsurance,
  adminUpdateCarInsurance,
  createQuotationFromEpolicy,
} from "../../../api/carInsuranceAPI";

const CAR_INSURANCE_GROUP_BY = "user,vehicle";

export const statusColor = {
  ACTIVE: "success",
  EXPIRED: "error",
  RENEWABLE: "warning",
};

export const carInsuranceSlice = createSlice({
  name: "carInsurance",
    initialState: {
     carInsurances: [],
     status: "idle",
     pagination: {},
     page: 0,
     rowsPerPage: 10,
     statusFilter: "ALL",
     filter: null,
     search: null,
     selectedId: null,
     selectedCarInsurance: null,
     detailModalOpen: false,
     deleteModalOpen: false,
     editModalOpen: false,
     createFromEpolicyModalOpen: false,
     successMessage: "",
     errorMessage: "",
   },
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
      // Reset selection when page changes
      state.selectedId = null;
      state.selectedCarInsurance = null;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
      // Reset selection when rows per page changes
      state.selectedId = null;
      state.selectedCarInsurance = null;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setCarInsurances: (state, action) => {
      state.carInsurances = action.payload;
      state.status = "success";
    },
    setSelectedId: (state, action) => {
      state.selectedId = action.payload;
      state.selectedCarInsurance = action.payload
        ? state.carInsurances.find((c) => c.id === action.payload) || null
        : null;
    },
    setSelectedCarInsurance: (state, action) => {
      // Direct setter for the full carInsurance object
      state.selectedCarInsurance = action.payload;
      state.selectedId = action.payload?.id || null;
    },
    setStatusFilter: (state, action) => {
      state.statusFilter = action.payload;
      // Reset selection when filter changes
      state.selectedId = null;
      state.selectedCarInsurance = null;
    },
    setSearch: (state, action) => {
      state.search = action.payload || null;
      // Reset selection when search changes
      state.selectedId = null;
      state.selectedCarInsurance = null;
    },
	showDetailModal: (state, action) => {
	  state.detailModalOpen = action.payload;
	},
    showDeleteModal: (state, action) => {
       state.deleteModalOpen = action.payload;
    },
     showEditModal: (state, action) => {
        state.editModalOpen = action.payload;
     },
     showCreateFromEpolicyModal: (state, action) => {
        state.createFromEpolicyModalOpen = action.payload;
     },
     setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
  },
});

export const {
   setPage,
   setRowsPerPage,
   setPagination,
   setCarInsurances,
   setSelectedId,
   setSelectedCarInsurance,
   setStatusFilter,
   setSearch,
   showDetailModal,
   showDeleteModal,
   showEditModal,
   showCreateFromEpolicyModal,
   setSuccessMessage,
   setErrorMessage,
} = carInsuranceSlice.actions;

export const selectState = (state) => state.carInsurance;

export const handleGetCarInsurance = () => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
     const response = await getAllCarInsurance({
       page: state.carInsurance.page,
       size: state.carInsurance.rowsPerPage,
       status: state.carInsurance.statusFilter,
       search: state.carInsurance.search,
       groupBy: CAR_INSURANCE_GROUP_BY,
     });
    const { content, ...pageable } = response.data;
    dispatch(setPagination(pageable));
    dispatch(setCarInsurances(content));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleDeleteCarInsurance = (id) => async (dispatch) => {
  dispatch(showLoading());

  try {
    await deleteCarInsurance(id);
    dispatch(showDeleteModal(false));
    dispatch(setSuccessMessage("Car insurance deleted successfully!"));
    dispatch(handleGetCarInsurance());
    dispatch(setSelectedId(null));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

export const handleUpdateCarInsurance = (id, request, ePolicyFile) => async (dispatch) => {
   dispatch(showLoading());

   try {
     const response = await adminUpdateCarInsurance(id, request, ePolicyFile);
     dispatch(showEditModal(false));
     dispatch(setSuccessMessage("Car insurance updated successfully!"));
     // Update the selected car insurance with the new data and open detail modal
     dispatch(setSelectedCarInsurance(response.data));
     dispatch(showDetailModal(true));
     dispatch(handleGetCarInsurance());
   } catch (error) {
     dispatch(setErrorMessage(error.message));
   } finally {
     dispatch(hideLoading());
   }
};

export const handleCreateQuotationFromEpolicy = (username, ePolicyFile) => async (dispatch) => {
   dispatch(showLoading());

   try {
     const response = await createQuotationFromEpolicy(username, ePolicyFile);
     dispatch(showCreateFromEpolicyModal(false));
     dispatch(setSuccessMessage("Motor quotation created successfully!"));
     dispatch(handleGetCarInsurance());
   } catch (error) {
     let errorMessage = "Failed to create motor quotation";

     // Handle specific error types
     if (error.code === 'ECONNABORTED') {
       errorMessage = "Request timeout - please try again";
     } else if (error.message === 'Network Error') {
       errorMessage = "Network error - please check your connection";
     } else if (error.response?.data?.message) {
       errorMessage = error.response.data.message;
     } else if (error.message) {
       errorMessage = error.message;
     }

     dispatch(setErrorMessage(errorMessage));
   } finally {
     dispatch(hideLoading());
   }
};

export default carInsuranceSlice.reducer;
